import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import useLanguage from '../hooks/useLanguage';
import GuestAssessmentQuestion, {
  EMPTY_GUEST_ASSESSMENT,
  guestAssessmentProblem,
  guestAssessmentPayload,
  guestAssessmentProblemKey
} from './GuestAssessmentQuestion';
import { taskRefusalKey } from '../utils/taskErrorCodes';

/**
 * Ops closing a turnover clean on the crew's behalf (L7) — the same question the crew card asks,
 * in the one dialog every ops close path opens (task card button or status, table row button or
 * dropdown). It collects the
 * answer and shows why a close was refused; the parent sends the close through `onConfirm`.
 *
 * Layout follows ReportIssueModal: centred on desktop, a full-screen sheet on phones.
 */
const GuestAssessmentModal = ({ show, task, onHide, onConfirm }) => {
  const { t, isArabic } = useLanguage();
  const [value, setValue] = useState(EMPTY_GUEST_ASSESSMENT);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState(null);    // our own translation
  const [errorText, setErrorText] = useState(null);  // a server refusal that carries no known code

  useEffect(() => {
    if (show && task) {
      setValue(EMPTY_GUEST_ASSESSMENT);
      setPhotos(task.photos || []);
      setErrorKey(null);
      setErrorText(null);
    }
  }, [show, task]);

  if (!task) return null;
  const prompt = task.guestAssessmentPrompt;

  const handleChange = (next) => {
    setValue(next);
    setErrorKey(null);
    setErrorText(null);
  };

  const handleConfirm = async () => {
    const problem = guestAssessmentProblem(value, prompt, photos);
    if (problem) {
      setErrorKey(guestAssessmentProblemKey(problem));
      return;
    }

    setSubmitting(true);
    setErrorKey(null);
    setErrorText(null);
    try {
      await onConfirm(guestAssessmentPayload(value));
    } catch (err) {
      const data = err?.response?.data;
      const key = taskRefusalKey(data);
      if (key) setErrorKey(key);
      else setErrorText(data?.error || t('tasks.card.completeFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const error = errorKey ? t(errorKey) : errorText;

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onHide}
      centered
      fullscreen="sm-down"
      dir={isArabic ? 'rtl' : 'ltr'}
      backdrop={submitting ? 'static' : true}
      keyboard={!submitting}
    >
      <Modal.Header closeButton={!submitting} dir={isArabic ? 'rtl' : 'ltr'}>
        <Modal.Title style={{ fontSize: '1.05rem', color: '#471396' }}>
          {t('tasks.guestAssessment.modalTitle')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body dir={isArabic ? 'rtl' : 'ltr'} style={{ textAlign: isArabic ? 'right' : 'left' }}>
        <div className="small text-muted mb-2">
          {task.displayLocationName && <div>📍 {task.displayLocationName}</div>}
          <div>{task.title}{task.ticketId && ` • ${task.ticketId}`}</div>
        </div>

        {error && (
          <div
            className="mb-2 p-2 rounded small fw-semibold"
            role="alert"
            style={{ background: '#fdecea', borderInlineStart: '3px solid #DC3545', color: '#090040' }}
          >
            {error}
          </div>
        )}

        <GuestAssessmentQuestion
          taskId={task._id}
          prompt={prompt}
          value={value}
          onChange={handleChange}
          photos={photos}
          onPhotoAdded={(photo) => setPhotos(prev => [...prev, photo])}
          disabled={submitting}
        />
      </Modal.Body>

      <Modal.Footer className="touch-modal-actions" dir={isArabic ? 'rtl' : 'ltr'}>
        <Button variant="light" className="touch-btn" onClick={onHide} disabled={submitting}>
          {t('tasks.guestAssessment.cancel')}
        </Button>
        <Button
          variant="primary"
          className="fw-bold touch-btn"
          style={{ background: '#471396', borderColor: '#471396' }}
          onClick={handleConfirm}
          disabled={submitting}
        >
          {submitting ? <Spinner animation="border" size="sm" /> : `✓ ${t('tasks.card.completeButton')}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GuestAssessmentModal;
